import React, {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import Calendar from "react-calendar";
import { Calendar as CalendarIcon, ChevronDown } from "lucide-react";
import { formatDisplayDate } from "../../utils/paymentsDateUtils";
import "react-calendar/dist/Calendar.css";
import "./AdminDatePicker.scss";

const POPOVER_WIDTH = 320;
const POPOVER_ESTIMATED_HEIGHT = 340;
const POPOVER_GAP = 6;
const VIEWPORT_PADDING = 8;

const getPopoverPosition = (triggerRect) => {
  let left = triggerRect.right - POPOVER_WIDTH;
  left = Math.max(VIEWPORT_PADDING, left);
  left = Math.min(left, window.innerWidth - POPOVER_WIDTH - VIEWPORT_PADDING);

  let top = triggerRect.bottom + POPOVER_GAP;
  if (top + POPOVER_ESTIMATED_HEIGHT > window.innerHeight - VIEWPORT_PADDING) {
    top = triggerRect.top - POPOVER_ESTIMATED_HEIGHT - POPOVER_GAP;
  }
  top = Math.max(VIEWPORT_PADDING, top);

  return { top, left };
};

const AdminDatePicker = ({
  label,
  value,
  onChange,
  minDate,
  maxDate,
  openPickerId,
  onOpenPickerIdChange,
}) => {
  const pickerId = useId();
  const rootRef = useRef(null);
  const popoverRef = useRef(null);
  const [popoverStyle, setPopoverStyle] = useState(null);
  const isOpen = openPickerId === pickerId;

  const updatePopoverPosition = () => {
    if (!rootRef.current) return;
    const rect = rootRef.current.getBoundingClientRect();
    setPopoverStyle(getPopoverPosition(rect));
  };

  useLayoutEffect(() => {
    if (!isOpen) {
      setPopoverStyle(null);
      return undefined;
    }

    updatePopoverPosition();

    const handleReposition = () => updatePopoverPosition();
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);

    return () => {
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      const target = event.target;
      if (
        rootRef.current?.contains(target) ||
        popoverRef.current?.contains(target)
      ) {
        return;
      }
      onOpenPickerIdChange(null);
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onOpenPickerIdChange(null);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onOpenPickerIdChange]);

  const toggle = () => {
    onOpenPickerIdChange(isOpen ? null : pickerId);
  };

  const handleChange = (nextDate) => {
    onChange(nextDate);
    onOpenPickerIdChange(null);
  };

  const displayValue = formatDisplayDate(value) || "—";

  const popover =
    isOpen && popoverStyle
      ? createPortal(
          <div
            ref={popoverRef}
            className="admin-date-picker__popover"
            role="dialog"
            aria-label={label}
            style={{
              top: `${popoverStyle.top}px`,
              left: `${popoverStyle.left}px`,
            }}
          >
            <Calendar
              onChange={handleChange}
              value={value}
              calendarType="Hebrew"
              locale="he-IL"
              minDate={minDate}
              maxDate={maxDate}
            />
          </div>,
          document.body,
        )
      : null;

  return (
    <div className="admin-date-picker" ref={rootRef}>
      <button
        type="button"
        className={`admin-date-picker__trigger${isOpen ? " is-open" : ""}`}
        onClick={toggle}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
      >
        <CalendarIcon aria-hidden size={18} strokeWidth={2} />
        <span className="admin-date-picker__text">
          <span className="admin-date-picker__label">{label}</span>
          <span className="admin-date-picker__value">{displayValue}</span>
        </span>
        <ChevronDown
          aria-hidden
          size={16}
          strokeWidth={2}
          className={`admin-date-picker__chevron${isOpen ? " is-open" : ""}`}
        />
      </button>

      {popover}
    </div>
  );
};

export default AdminDatePicker;
